import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  fileHistoriesQueryOptions,
  useRestoreFileHistory,
} from "@/hooks/use-file-history";
import { HistoryTable } from "@/components/file-history/history-table";
import { HistoryDetailSheet } from "@/components/file-history/history-detail-sheet";
import { RestoreConfirmDialog } from "@/components/file-history/restore-confirm-dialog";
import type { FileHistoryItem } from "@/types/file-history";

function errorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message.toLowerCase() : "";
  if (raw.includes("not found")) {
    return "해당 파일을 찾을 수 없습니다.";
  }
  return "버전 기록을 불러오지 못했습니다.";
}

export default function FileHistoryViewerPage() {
  const [input, setInput] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<FileHistoryItem | null>(
    null
  );
  const [restoreTarget, setRestoreTarget] = useState<FileHistoryItem | null>(
    null
  );

  const query = useQuery(
    useMemo(() => fileHistoriesQueryOptions(submitted), [submitted])
  );
  const restoreMutation = useRestoreFileHistory(submitted);

  const handleSearch = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setSelectedHistory(null);
    setRestoreTarget(null);
    setSubmitted(trimmed);
  }, [input]);

  const handleConfirmRestore = useCallback(() => {
    if (!restoreTarget) return;
    restoreMutation.mutate(restoreTarget._id, {
      onSuccess: () => setRestoreTarget(null),
    });
  }, [restoreTarget, restoreMutation]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">파일 버전 기록</h1>
        <p className="text-muted-foreground leading-normal">
          파일 ID로 버전 기록을 조회하고, 특정 시점으로 복원합니다.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">파일 조회</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="file-history-file-id">파일 ID</Label>
            <div className="flex gap-2">
              <Input
                id="file-history-file-id"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="files 컬렉션의 _id"
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSearch();
                }}
              />
              <Button onClick={handleSearch} disabled={!input.trim()}>
                <Search className="mr-2 h-4 w-4" />
                조회
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {submitted && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">버전 기록</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {query.isLoading && (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}
            {query.isError && (
              <div className="rounded border p-4 text-sm text-destructive">
                {errorMessage(query.error)}
              </div>
            )}
            {query.data && (
              <>
                <div className="text-xs text-muted-foreground">
                  총 {query.data.length.toLocaleString()}개 (최신순) · 행을
                  클릭하면 본문을 봅니다
                </div>
                <HistoryTable
                  histories={query.data}
                  onSelect={setSelectedHistory}
                  onRestore={setRestoreTarget}
                  isRestoring={restoreMutation.isPending}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <HistoryDetailSheet
        history={selectedHistory}
        onClose={() => setSelectedHistory(null)}
        onRestore={(history) => {
          setSelectedHistory(null);
          setRestoreTarget(history);
        }}
      />

      <RestoreConfirmDialog
        target={restoreTarget}
        isRestoring={restoreMutation.isPending}
        onConfirm={handleConfirmRestore}
        onCancel={() => setRestoreTarget(null)}
      />
    </div>
  );
}
